import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAppConfig } from '@state';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useUserAuthentication,
} from '@ohif/ui-next';

import { getSupabaseClient } from '../../utils/supabaseClient';
import { STUDY_LIST_PATH } from '../../constants/routes';

function SignUpPage() {
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();
  const [{ user }] = useUserAuthentication();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const afterAuthPath = useMemo(
    () => (appConfig.showStudyList ? STUDY_LIST_PATH : '/'),
    [appConfig.showStudyList]
  );

  useEffect(() => {
    if (user) {
      navigate(afterAuthPath, { replace: true });
    }
  }, [user, navigate, afterAuthPath]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const client = getSupabaseClient();
    if (!client) {
      setError('Supabase is not configured.');
      return;
    }
    setSubmitting(true);
    const { data, error: signUpError } = await client.auth.signUp({ email, password });
    setSubmitting(false);
    if (signUpError) {
      setError(signUpError.message);
      return;
    }
    if (data.session) {
      navigate(afterAuthPath, { replace: true });
      return;
    }
    setInfo('Check your email to confirm your account before signing in.');
  };

  return (
    <div className="bg-background text-foreground flex min-h-full w-full items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create account</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={onSubmit}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={ev => setEmail(ev.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={ev => setPassword(ev.target.value)}
                required
                minLength={6}
              />
            </div>
            {error ? <p className="text-destructive text-sm">{error}</p> : null}
            {info ? <p className="text-muted-foreground text-sm">{info}</p> : null}
            <Button
              type="submit"
              disabled={submitting}
              name="signup"
            >
              {submitting ? 'Creating account…' : 'Sign up'}
            </Button>
            <p className="text-muted-foreground text-sm">
              Already have an account?{' '}
              <Link
                className="text-highlight underline"
                to="/sign-in"
              >
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default SignUpPage;
